import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminAccount, WorkspacePlan } from '@katsquare/types';
import { formatBytes } from '../../lib/format';
import { Button, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { deleteAdminAccount, getAdminAccounts, setAdminAccountPlan } from '../../lib/api';

const PLANS: WorkspacePlan[] = ['FREE', 'PRO', 'BUSINESS'];

export function AdminAccountsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const accounts = useQuery({ queryKey: ['admin', 'accounts'], queryFn: getAdminAccounts });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
  };
  const changePlan = useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: WorkspacePlan }) => setAdminAccountPlan(id, plan),
    onSuccess: (a) => {
      invalidate();
      toast(`${a.email} moved to the ${a.plan[0] + a.plan.slice(1).toLowerCase()} plan`);
    },
    onError: (e: Error) => toast(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteAdminAccount(id),
    onSuccess: () => {
      setConfirmId(null);
      invalidate();
      toast('Account deleted');
    },
    onError: (e: Error) => toast(e.message),
  });

  if (accounts.isPending) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (accounts.isError || !accounts.data) {
    return <p className="text-sm text-red-600">Couldn’t load accounts.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-neutral-900">Accounts</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Every user account and its subscription. Plans are billed per account.
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-neutral-100 text-left text-xs font-medium text-neutral-500">
              <th className="px-4 py-2.5">Account</th>
              <th className="px-4 py-2.5">Plan</th>
              <th className="px-4 py-2.5">Time left</th>
              <th className="px-4 py-2.5 text-right">Workspaces</th>
              <th className="px-4 py-2.5 text-right">Storage</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.data.map((a: AdminAccount) => {
              const pending = changePlan.isPending && changePlan.variables?.id === a.id;
              return (
                <tr key={a.id} className="border-b border-neutral-50 last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-medium text-neutral-900">
                      {a.name}
                      {a.isSuperAdmin && (
                        <span className="rounded bg-neutral-900 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-400">{a.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <select
                        value={a.plan}
                        disabled={pending || a.isSuperAdmin}
                        onChange={(e) => changePlan.mutate({ id: a.id, plan: e.target.value as WorkspacePlan })}
                        className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-700 outline-none focus:border-neutral-900 disabled:opacity-50"
                      >
                        {PLANS.map((p) => (
                          <option key={p} value={p}>
                            {p[0] + p.slice(1).toLowerCase()}
                          </option>
                        ))}
                      </select>
                      {pending && <Spinner />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <TimeLeft account={a} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-600">{a.workspaces}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-600">
                    {formatBytes(a.storageBytes)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {a.isSuperAdmin ? (
                      <span className="text-xs text-neutral-300">—</span>
                    ) : confirmId === a.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-red-600">Delete account &amp; its workspaces?</span>
                        <Button
                          variant="danger"
                          onClick={() => remove.mutate(a.id)}
                          disabled={remove.isPending}
                          className="text-xs"
                        >
                          {remove.isPending ? <Spinner /> : 'Delete'}
                        </Button>
                        <button
                          type="button"
                          onClick={() => setConfirmId(null)}
                          className="text-xs text-neutral-400 hover:text-neutral-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmId(a.id)}
                        className="text-xs font-medium text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {accounts.data.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-neutral-400">
                  No accounts yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * How long this account has: days of paid plan remaining, days until its files
 * are purged if it has lapsed, or "Unlimited" for a plan an admin granted —
 * those carry no paid period, so nothing expires them.
 */
function TimeLeft({ account }: { account: AdminAccount }) {
  if (account.graceEndsAt) {
    const days = daysUntil(account.graceEndsAt);
    return (
      <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
        {days <= 0 ? 'Files purged today' : `Files deleted in ${days}d`}
      </span>
    );
  }
  if (account.plan === 'FREE') return <span className="text-xs text-neutral-400">—</span>;
  if (!account.currentPeriodEnd) {
    return (
      <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
        Unlimited · admin
      </span>
    );
  }
  const days = daysUntil(account.currentPeriodEnd);
  return (
    <span className="text-xs tabular-nums text-neutral-600">
      {days <= 0 ? 'Expired' : `${days} day${days === 1 ? '' : 's'} left`}
      <span className="ml-1 text-neutral-400">
        (
        {new Date(account.currentPeriodEnd).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'short',
        })}
        )
      </span>
    </span>
  );
}

/** Whole days from now until an ISO instant, rounded up; never negative-zero. */
function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}
